import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Upload, Loader2, Film, Image as ImageIcon, CheckCircle2, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, getToken } from "@/lib/api";
import { takePhoto, isNative } from "@/lib/native";
import type { Product, MediaAsset } from "@/lib/types";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MAX_SIZE_MB = 200;
const ACCEPTED_IMAGE = ["image/jpeg", "image/png", "image/webp"];
const ACCEPTED_VIDEO = ["video/mp4", "video/quicktime", "video/webm"];

export const UploadContent: React.FC = () => {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [selectedProduct, setSelectedProduct] = useState<string | undefined>(
    params.get("product_id") || undefined,
  );
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<MediaAsset | null>(null);

  const products = useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
  });

  const kind = useMemo<"image" | "video" | null>(() => {
    if (!file) return null;
    if (ACCEPTED_IMAGE.includes(file.type)) return "image";
    if (ACCEPTED_VIDEO.includes(file.type)) return "video";
    return null;
  }, [file]);

  const handleFile = (f: File | undefined | null) => {
    setResult(null);
    setProgress(0);
    if (!f) return;
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_SIZE_MB} MB`);
      return;
    }
    if (![...ACCEPTED_IMAGE, ...ACCEPTED_VIDEO].includes(f.type)) {
      toast.error("Unsupported file type. Use JPG/PNG/WEBP or MP4/MOV/WEBM");
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file || !kind) return;
    setUploading(true);
    setProgress(0);
    try {
      // 1) Create upload session
      const session = await api.post("/media/upload-session", {
        original_filename: file.name,
        mime_type: file.type,
        kind,
        product_id: selectedProduct || null,
      });
      const uploadUrl = session.data.upload_url;

      // 2) Upload directly to the returned URL (backend-mediated signed URL)
      const form = new FormData();
      form.append("file", file);
      const resp = await axios.post(uploadUrl, form, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      });
      setResult(resp.data);
      toast.success("Upload complete. Thumbnails ready.");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <DashboardLayout title="Upload content" subtitle="Attach media to a product or content record. Thumbnails are generated automatically." seoPath="/upload" seoDescription="Upload photos and videos, attach them to a product, and let SociBaba generate thumbnails automatically.">
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 sg-card p-6">
          <div className="sg-label mb-3">Media file</div>
          <label
            data-testid="upload-dropzone"
            className={`block border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
              file ? "border-[color:var(--sg-fg)] bg-[color:var(--sg-muted-bg)]" : "border-[color:var(--sg-border)] hover:border-[color:var(--sg-fg)]"
            }`}
          >
            <input
              type="file"
              accept={[...ACCEPTED_IMAGE, ...ACCEPTED_VIDEO].join(",")}
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Upload className="h-8 w-8 mx-auto text-[color:var(--sg-muted)]" strokeWidth={1.5} />
            <div className="font-display text-lg font-semibold mt-3 tracking-tight">
              {file ? file.name : "Drop or click to select"}
            </div>
            <div className="text-xs text-[color:var(--sg-muted)] mt-1">
              JPG, PNG, WEBP, MP4, MOV, WEBM · up to {MAX_SIZE_MB} MB
            </div>
            {file && (
              <div className="mt-3 flex items-center justify-center gap-2 text-sm text-[color:var(--sg-muted)]">
                {kind === "image" ? <ImageIcon className="h-4 w-4" /> : <Film className="h-4 w-4" />}
                {(file.size / (1024 * 1024)).toFixed(1)} MB · {file.type}
              </div>
            )}
          </label>

          <NativeCameraButton onCaptured={handleFile} />

          {uploading && (
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-xs text-[color:var(--sg-muted)]">
                <span>Uploading…</span>
                <span data-testid="upload-progress-text">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {result && (
            <div className="mt-6 sg-card p-4 border-[color:var(--sg-success)] bg-emerald-50/40">
              <div className="flex items-center gap-2 mb-2 text-emerald-800">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm font-semibold">
                  Uploaded & processed
                </span>
              </div>
              <div className="text-xs text-[color:var(--sg-muted)]">
                {result.thumbnails.length} thumbnail{result.thumbnails.length === 1 ? "" : "s"} generated.
              </div>
              <div className="flex gap-2 mt-3">
                {result.thumbnails.slice(0, 4).map((t) => (
                  <img
                    key={t.thumb_id}
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/media/${result.media_id}/thumb/${t.thumb_id}?auth=${encodeURIComponent(getToken() || "")}`}
                    alt="Thumb"
                    loading="lazy"
                    decoding="async"
                    className="h-16 w-24 object-cover rounded border border-[color:var(--sg-border)]"
                  />
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                {result.product_id && (
                  <Button
                    size="sm"
                    onClick={() => nav(`/products/${result.product_id}`)}
                    className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
                  >
                    Open product
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setFile(null);
                    setResult(null);
                    setProgress(0);
                  }}
                >
                  Upload another
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="sg-card p-6">
            <div className="sg-label mb-3">Link to product</div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Product</Label>
            <Select value={selectedProduct} onValueChange={setSelectedProduct}>
              <SelectTrigger data-testid="upload-product-select" className="bg-white">
                <SelectValue placeholder="(Optional) Choose a product" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {(products.data || []).map((p) => (
                  <SelectItem key={p.product_id} value={p.product_id}>
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[color:var(--sg-muted)] mt-2 leading-relaxed">
              Linking media to a product lets SociBaba attribute engagement, leads and complaints back to the source.
            </p>
          </div>

          <Button
            data-testid="upload-submit"
            disabled={!file || uploading}
            onClick={handleUpload}
            className="w-full h-11 bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white font-semibold"
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Start upload</>
            )}
          </Button>

          <div className="sg-card p-6">
            <div className="sg-label mb-2">Media lifecycle</div>
            <ul className="text-sm text-[color:var(--sg-muted)] space-y-2 leading-relaxed">
              <li>1. File uploaded directly via secure session URL.</li>
              <li>2. Metadata + auto thumbnails generated.</li>
              <li>3. Publish to selected platforms.</li>
              <li>4. Raw video auto-deleted after all targets succeed — thumbnails stay.</li>
            </ul>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

/**
 * Native camera trigger — only rendered inside the Capacitor iOS/Android
 * shell so the button doesn't confuse desktop users.
 */
const NativeCameraButton: React.FC<{ onCaptured: (file: File) => void }> = ({ onCaptured }) => {
  const [visible, setVisible] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    isNative().then(setVisible);
  }, []);

  if (!visible) return null;

  const trigger = async () => {
    setBusy(true);
    try {
      const photo = await takePhoto();
      if (!photo?.dataUrl) return;
      // Convert data URL → File so it flows through the existing handleFile pipeline.
      const [meta, b64] = photo.dataUrl.split(",");
      const mime = /data:(.*?);/.exec(meta)?.[1] || `image/${photo.format}`;
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      const file = new File([bytes], `capture-${Date.now()}.${photo.format}`, { type: mime });
      onCaptured(file);
    } catch (e: any) {
      toast.error(e?.message || "Camera unavailable");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={trigger}
      disabled={busy}
      data-testid="upload-native-camera"
      className="mt-3 w-full"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Camera className="h-4 w-4 mr-1.5" />}
      Take photo with camera
    </Button>
  );
};
